import { useState, type CSSProperties, type ReactNode } from "react";

/** Portato da `components/core/Button.jsx` del design system. */

const VARIANTS = {
  primary: {
    background: "var(--ink-900)",
    color: "var(--text-inverse)",
    border: "1px solid transparent",
  },
  accent: {
    background: "var(--lime-400)",
    color: "var(--text-on-lime)",
    border: "1px solid transparent",
  },
  secondary: {
    background: "var(--white)",
    color: "var(--text-primary)",
    border: "1px solid var(--border-strong)",
  },
  ghost: { background: "transparent", color: "var(--ink-700)", border: "1px solid transparent" },
  danger: {
    background: "var(--coral-100)",
    color: "var(--coral-500)",
    border: "1px solid transparent",
  },
} satisfies Record<string, CSSProperties>;

const SIZES = {
  sm: { height: 28, padding: "0 12px", fontSize: 12 },
  md: { height: 36, padding: "0 17px", fontSize: 13.5 },
  lg: { height: 44, padding: "0 22px", fontSize: 14.5 },
} satisfies Record<string, CSSProperties>;

export type ButtonVariant = keyof typeof VARIANTS;
export type ButtonSize = keyof typeof SIZES;

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  disabled?: boolean;
  /** `submit` quando il pulsante conclude un form: fa funzionare l'invio da tastiera. */
  type?: "button" | "submit";
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  onClick?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
}

export function Button({
  variant = "primary",
  size = "md",
  full = false,
  disabled = false,
  type = "button",
  iconLeft,
  iconRight,
  onClick,
  children,
  style,
}: ButtonProps) {
  const v = VARIANTS[variant];
  const s = SIZES[size];
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        width: full ? "100%" : undefined,
        fontFamily: "var(--font-text)",
        fontWeight: 700,
        letterSpacing: "-0.1px",
        borderRadius: "var(--radius-pill)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
        // Al press il pulsante scende appena: nessuno scale, il colore non cambia mai.
        transform: pressed && !disabled ? "translateY(var(--press-shift))" : "translateY(0)",
        transition: "transform var(--duration-instant) var(--ease-standard)",
        boxShadow: "none",
        ...s,
        ...v,
        ...style,
      }}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}
